import React, { useMemo, useEffect, useState } from 'react';
import { Title, Meta, Link } from 'react-head';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { APP_URL } from '../config';
import { useCourseDataForSEO } from '../hooks/useCourseDataForSEO';

const SEO = () => {
  const { t, i18n } = useTranslation('seo');
  const location = useLocation();
  const [page, setPage] = useState('home');
  
  const { courseData: seoCourseData } = useCourseDataForSEO();
  
  useEffect(() => {
    const pathname = location.pathname;
    let pageType = 'home';
    
    if (pathname === '/') pageType = 'home';
    else if (pathname === '/courses') pageType = 'courses';
    else if (pathname.startsWith('/courses/') && pathname.split('/').length === 3) pageType = 'courseDetail';
    else if (pathname === '/dashboard') pageType = 'dashboard';
    else if (pathname === '/login') pageType = 'login';
    else if (pathname === '/register') pageType = 'register';
    else if (pathname === '/leaderboard') pageType = 'leaderboard';
    else if (pathname === '/learning-paths') pageType = 'learningPaths';
    else if (pathname === '/profile') pageType = 'profile';
    
    setPage(pageType);
  }, [location.pathname]);
  
  const seoData = useMemo(() => {
    const pageTranslations = t(page, { returnObjects: true });
    
    let finalTitle = pageTranslations?.title || t('home.title');
    let finalDescription = pageTranslations?.description || t('home.description');
    let finalKeywords = pageTranslations?.keywords || t('home.keywords');
    let finalImage = 'https://lh7-rt.googleusercontent.com/docsz/AD_4nXfkFz2-r2MiaHNrdA0KwuXdzbkH8309jX5w6lM5CGIbTbvVE3Obac_v39K5jM8lMewp9MXa0fg_gJJSEy04Or3g_Fm2dpKitXdx9Vd5QXzGJwsRRDws97jbU3SdixGFgGh-uoO5sQ?key=UmeuTJy5AiuQaY-gYAJHrA';
    let finalUrl = location.pathname;
    
    if (page === 'courseDetail' && seoCourseData) {
      finalTitle = `${seoCourseData.title || 'Course'}${t('courseDetail.titleSuffix')}`;
      finalDescription = seoCourseData.description || finalDescription;
      finalKeywords = seoCourseData.keywords || `${seoCourseData.title || ''}, ${t('courseDetail.keywords')}, ${seoCourseData.difficulty || ''}`;
      if (seoCourseData.image) finalImage = seoCourseData.image;
      if (seoCourseData.url) finalUrl = seoCourseData.url;
    }
    
    return {
      title: finalTitle,
      description: finalDescription,
      keywords: finalKeywords,
      image: finalImage,
      url: finalUrl
    };
  }, [page, seoCourseData, location.pathname, t]);
  
  const fullUrl = seoData.url.startsWith('http') ? seoData.url : `${APP_URL}${seoData.url}`;
  const fullImageUrl = seoData.image.startsWith('http') ? seoData.image : `${APP_URL}${seoData.image}`;

  return (
    <>
      <Title>{seoData.title}</Title>
      <Meta name="description" content={seoData.description} />
      <Meta name="keywords" content={seoData.keywords} />
      <Meta name="author" content="Learning Platform Team" />
      <Meta name="robots" content="index, follow" />
      <Meta name="language" content={i18n.language} />
      
      <Link rel="canonical" href={fullUrl} />
      
      <Meta property="og:title" content={seoData.title} />
      <Meta property="og:description" content={seoData.description} />
      <Meta property="og:type" content="website" />
      <Meta property="og:url" content={fullUrl} />
      <Meta property="og:image" content={fullImageUrl} />
      <Meta property="og:image:width" content="1200" />
      <Meta property="og:image:height" content="630" />
      <Meta property="og:site_name" content="Learning Platform" />
      <Meta property="og:locale" content={i18n.language === 'ru' ? 'ru_RU' : 'en_US'} />
      
      <Meta name="twitter:card" content="summary_large_image" />
      <Meta name="twitter:title" content={seoData.title} />
      <Meta name="twitter:description" content={seoData.description} />
      <Meta name="twitter:image" content={fullImageUrl} />
    </>
  );
};

export default SEO;
